import { useQueries } from '@tanstack/react-query';
import { requestHandler } from 'lib/api';
import AlertBanner from 'lib/designSystem/AlertBanner';
import { Evaluation } from 'lib/types/models';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import EvaluationGroupCard from './EvaluationGroupCard';
import EvaluationGroupCardSkeleton from './EvaluationGroupCardSkeleton';
import EvaluationListDialog from './EvaluationListDialog';

export type RequestEntry = {
  requestId: number | string | null;
  requestType: string;
};

interface EvaluationReviewsProps {
  requestEntries: RequestEntry[];
  emptyMessage?: string;
}

const EvaluationReviews: React.FC<EvaluationReviewsProps> = ({
  requestEntries,
  emptyMessage,
}) => {
  const dispatch = useDispatch();
  const [selectedGroup, setSelectedGroup] = useState<Evaluation[] | null>(null);

  const queries = useQueries({
    queries: requestEntries.map((entry) => ({
      queryKey: [
        'decisionEngine',
        'requestEvaluations',
        entry.requestType,
        entry.requestId,
      ],
      queryFn: async (): Promise<Record<string, Evaluation[]>> => {
        const response = await requestHandler({
          method: 'get',
          url: `internal/decisionEngine/requestType/${entry.requestType}/requestId/${entry.requestId}`,
          dispatch,
        });
        if (response) return response.data;
        return {};
      },
      enabled: !!entry.requestType && !!entry.requestId,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 5,
      retry: 1,
    })),
  });

  const isLoading = queries.some((q) => q.isPending);

  if (isLoading) {
    return (
      <div>
        {[1, 2].map((i) => (
          <EvaluationGroupCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const evaluationGroups: Evaluation[][] = queries.flatMap((q) =>
    Object.values(q.data ?? {}),
  );

  const hasAny = evaluationGroups.some((group) => group.length > 0);

  if (!hasAny) {
    return (
      <AlertBanner
        severity="warning"
        title="Motor de riesgo no ejecutado"
        message={
          emptyMessage ??
          'Para obtener una evaluación completa del riesgo, es necesario ejecutar el motor de decisión automatizado. Una vez ejecutado, podrás ver el historial completo de evaluaciones.'
        }
      />
    );
  }

  return (
    <div>
      {evaluationGroups.map(
        (group, index) =>
          group.length > 0 && (
            <EvaluationGroupCard
              key={index}
              evaluations={group}
              onClick={() => setSelectedGroup(group)}
            />
          ),
      )}

      <EvaluationListDialog
        open={selectedGroup !== null}
        onClose={() => setSelectedGroup(null)}
        evaluations={selectedGroup ?? []}
      />
    </div>
  );
};

export default EvaluationReviews;
